import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Phone, Mail, StickyNote, CalendarCheck, ClipboardList, Pencil, CircleDashed, CircleDot, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import DatePicker from "@/components/DatePicker";

const TYPE_ICON = { call: Phone, email: Mail, note: StickyNote, meeting: CalendarCheck, task: ClipboardList };

const PRIORITY_META = {
  urgent:    { label: "Urgent",    className: "bg-destructive/15 text-destructive" },
  important: { label: "Important", className: "bg-amber-500/15 text-amber-700" },
  medium:    { label: "Medium",    className: "bg-primary/10 text-primary" },
  low:       { label: "Low",       className: "bg-secondary text-muted-foreground" },
};

const STATUS_META = {
  not_started: { label: "Not Started", icon: CircleDashed, className: "text-muted-foreground" },
  in_progress: { label: "In Progress", icon: CircleDot,    className: "text-primary" },
  completed:   { label: "Completed",   icon: CheckCircle2, className: "text-emerald-600" },
};

const EMPTY = {
  title: "", type: "task", priority: "medium", status: "not_started",
  start_date: "", due_date: "", assignee_id: "", notes: "",
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
};

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("open");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    // "open" → both not_started + in_progress in a single query
    let statusParam = null;
    if (filter === "open")      statusParam = "not_started,in_progress";
    else if (filter === "completed") statusParam = "completed";
    const params = statusParam ? { status: statusParam } : {};
    const [t, u] = await Promise.all([api.get("/tasks", { params }), api.get("/users")]);
    setTasks(t.data); setUsers(u.data);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u.name])), [users]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({
      title: t.title || "", type: t.type || "task", priority: t.priority || "medium",
      status: t.status || "not_started",
      start_date: t.start_date || "", due_date: t.due_date || "",
      assignee_id: t.assignee_id || "", notes: t.notes || "",
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        assignee_id: form.assignee_id || null,
        start_date: form.start_date || null,
        due_date: form.due_date || null,
      };
      if (editingId) {
        await api.patch(`/tasks/${editingId}`, payload);
        toast.success("Task updated");
      } else {
        await api.post("/tasks", payload);
        toast.success("Task created");
      }
      setOpen(false); setForm(EMPTY); setEditingId(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const toggle = async (id) => { await api.post(`/tasks/${id}/toggle`); load(); };
  const remove = async (id) => {
    if (!confirm("Delete this task?")) return;
    try {
      await api.delete(`/tasks/${id}`);
      toast.success("Task removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const FILTERS = [
    { k: "open",      label: "Open" },
    { k: "completed", label: "Completed" },
    { k: "all",       label: "All" },
  ];

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="tasks-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-2">Activity</div>
          <h1 className="text-3xl tracking-tight font-medium">Tasks</h1>
          <p className="text-sm text-muted-foreground mt-1">Follow-ups, calls, meetings and notes.</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingId(null); setForm(EMPTY); } }}>
          <DialogTrigger asChild>
            <Button data-testid="new-task-btn" onClick={openCreate} className="bg-primary hover:bg-primary/90 text-primary-foreground">
              <Plus className="h-4 w-4 mr-1.5" /> New task
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit task" : "New task"}</DialogTitle>
              <DialogDescription className="sr-only">
                Fill in the task details and dates.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              <div className="space-y-1.5">
                <Label>Title</Label>
                <Input data-testid="task-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label>Type</Label>
                  <select data-testid="task-type" className="h-9 border border-input rounded-md px-2 text-sm bg-background w-full"
                    value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                    <option value="task">Task</option><option value="call">Call</option>
                    <option value="email">Email</option><option value="meeting">Meeting</option><option value="note">Note</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label>Priority</Label>
                  <select data-testid="task-priority" className="h-9 border border-input rounded-md px-2 text-sm bg-background w-full"
                    value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                    <option value="urgent">Urgent</option>
                    <option value="important">Important</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label>Assignee</Label>
                  <select data-testid="task-assignee" className="h-9 border border-input rounded-md px-2 text-sm bg-background w-full"
                    value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })}>
                    <option value="">— Me —</option>
                    {users.map((u) => (<option key={u.id} value={u.id}>{u.name}</option>))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Start date</Label>
                  <DatePicker
                    testId="task-start-date"
                    value={form.start_date}
                    onChange={(v) => setForm({ ...form, start_date: v || "" })}
                    placeholder="Pick start date"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Due date</Label>
                  <DatePicker
                    testId="task-due-date"
                    value={form.due_date}
                    onChange={(v) => setForm({ ...form, due_date: v || "" })}
                    placeholder="Pick due date"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <select data-testid="task-status" className="h-9 border border-input rounded-md px-2 text-sm bg-background w-full"
                  value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="not_started">Not Started</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="task-cancel">Cancel</Button>
              <Button onClick={save} disabled={saving || !form.title} data-testid="task-save" className="bg-primary text-primary-foreground hover:bg-primary/90">
                {saving ? "Saving…" : editingId ? "Save" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-2 border-b border-border overflow-x-auto">
        {FILTERS.map(({ k, label }) => (
          <button
            key={k}
            data-testid={`filter-${k}`}
            onClick={() => setFilter(k)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors duration-150 whitespace-nowrap ${
              filter === k ? "border-primary text-primary font-medium" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="card-flat overflow-x-auto">
        <div className="divide-y divide-border min-w-[720px]">
          {tasks.length === 0 && (<div className="px-4 py-8 text-center text-muted-foreground text-sm">No tasks here.</div>)}
          {tasks.map((t) => {
            const TypeIcon = TYPE_ICON[t.type] || ClipboardList;
            const stat = STATUS_META[t.status] || STATUS_META.not_started;
            const StatusIcon = stat.icon;
            const prio = PRIORITY_META[t.priority] || PRIORITY_META.medium;
            const isDone = t.status === "completed";
            return (
              <div key={t.id} data-testid={`task-row-${t.id}`} className="px-4 py-3 flex items-center gap-4 hover:bg-accent/40 transition-colors duration-150">
                <button
                  data-testid={`toggle-task-${t.id}`}
                  onClick={() => toggle(t.id)}
                  className={`inline-flex items-center justify-center h-6 w-6 rounded hover:bg-accent transition-colors ${stat.className}`}
                  title={`Status: ${stat.label} (click to cycle)`}
                >
                  <StatusIcon className="h-5 w-5" strokeWidth={1.75} />
                </button>
                <TypeIcon className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                <div className="flex-1 min-w-0">
                  <div className={`text-sm truncate ${isDone ? "line-through text-muted-foreground" : ""}`}>{t.title}</div>
                  {(t.start_date || t.due_date) && (
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {t.start_date && <span data-testid={`task-start-${t.id}`}>Start: {fmtDate(t.start_date)}</span>}
                      {t.start_date && t.due_date && " · "}
                      {t.due_date && <span data-testid={`task-due-${t.id}`}>Due: {fmtDate(t.due_date)}</span>}
                    </div>
                  )}
                </div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-medium ${prio.className}`} data-testid={`task-priority-badge-${t.id}`}>
                  {prio.label}
                </span>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${stat.className} bg-secondary/60 hidden sm:inline-block`} data-testid={`task-status-badge-${t.id}`}>
                  {stat.label}
                </span>
                <div className="text-xs text-muted-foreground w-32 truncate text-right hidden md:block">{userMap[t.assignee_id] || "Unassigned"}</div>
                <button
                  data-testid={`edit-task-${t.id}`}
                  onClick={() => openEdit(t)}
                  className="text-muted-foreground hover:text-primary transition-colors duration-150"
                  aria-label="Edit task"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  data-testid={`delete-task-${t.id}`}
                  onClick={() => remove(t.id)}
                  className="text-muted-foreground hover:text-destructive transition-colors duration-150"
                  aria-label="Delete task"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
